/**
 * Widget Registry
 *
 * Maps ModuleId to actual React components
 * This is the single source of truth for all dashboard modules
 */

import React from "react";
import { ModuleId } from "../layouts/types";

// Import existing widgets
import { PortfolioValueCard } from "./PortfolioValueCard";
import { ConnectedExchangesWidget } from "./ConnectedExchangesWidget";
import { RecentTransactionsWidget } from "./RecentTransactionsWidget";
import { TaxDashboardWidget } from "./TaxDashboardWidget";
import { TaxSummaryWidget } from "./TaxSummaryWidget";
import { TopHoldingsWidget } from "./TopHoldingsWidget";
import { RecentTaxDisposalsWidget } from "./RecentTaxDisposalsWidget";
import { TaxTipsWidget } from "./TaxTipsWidget";
import { UpgradeWidget } from "./UpgradeWidget";
import { BridgeMatchesWidget } from "./BridgeMatchesWidget";
import { OnboardingChecklistWidget } from "./OnboardingChecklistWidget";
import { TaxTasksWidget } from "./TaxTasksWidget";
import { AlertsCenterWidget } from "./AlertsCenterWidget";
import { AppDownloadBanner } from "./AppDownloadBanner";
import { FilingCountdownWidget } from "./FilingCountdownWidget";

// Registry: ModuleId → React Component
export const WIDGET_REGISTRY: Record<ModuleId, React.ComponentType<any>> = {
  [ModuleId.HeroOverview]: PortfolioValueCard,

  [ModuleId.OnboardingChecklist]: OnboardingChecklistWidget,

  [ModuleId.BridgeReview]: BridgeMatchesWidget,

  [ModuleId.ConnectedExchanges]: ConnectedExchangesWidget,

  [ModuleId.RecentActivity]: RecentTransactionsWidget,

  [ModuleId.TaxDashboard]: TaxDashboardWidget,

  [ModuleId.TaxSummary]: TaxSummaryWidget,

  [ModuleId.TaxTasks]: TaxTasksWidget,

  [ModuleId.FilingCountdown]: FilingCountdownWidget,

  [ModuleId.AlertsCenter]: AlertsCenterWidget,

  [ModuleId.TopHoldings]: TopHoldingsWidget,

  [ModuleId.RecentTaxDisposals]: RecentTaxDisposalsWidget,

  [ModuleId.Tips]: TaxTipsWidget,

  [ModuleId.UpgradeCTA]: UpgradeWidget,

  [ModuleId.AppDownload]: AppDownloadBanner,
};

/**
 * Get widget component for a module ID
 */
export function getWidget(moduleId: ModuleId): React.ComponentType<any> | null {
  return WIDGET_REGISTRY[moduleId] || null;
}

/**
 * Check if a widget is implemented (not a placeholder)
 */
export function isWidgetImplemented(moduleId: ModuleId): boolean {
  const implemented = [
    ModuleId.HeroOverview,
    ModuleId.OnboardingChecklist,
    ModuleId.ConnectedExchanges,
    ModuleId.RecentActivity,
    ModuleId.TaxDashboard,
    ModuleId.TaxSummary,
    ModuleId.TaxTasks,
    ModuleId.TopHoldings,
    ModuleId.RecentTaxDisposals,
    ModuleId.Tips,
    ModuleId.UpgradeCTA,
    ModuleId.BridgeReview,
    ModuleId.FilingCountdown,
    ModuleId.AlertsCenter,
    ModuleId.AppDownload,
  ];
  return implemented.includes(moduleId);
}
